/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Empty,
  Space,
  Spin,
  Typography,
  Collapse,
  Tag,
  Table,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateSaveCropMutation,
  useGetSaveCropTaxonomyTreeQuery,
  useGetSaveCropsQuery,
} from '../../api/cropsApi';
import type { Crop } from '../../models/Crop';
import { toastSuccess } from '../../utils/toast';
import { CropFormModal } from '../../components/crops/CropFormModal';
import { useCanIDo, useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import { buildScenarioCropSummaries, type ScenarioCropSummary } from '../../utils/scenarioWorldAdapters';
import './FarmCropsPage.css';

const { Title, Text, Paragraph } = Typography;

interface CropWithTaxonomy extends Crop {
  familyId?: string | number | null;
  familyName?: string | null;
  familyLatinName?: string | null;
  genusId?: string | number | null;
  genusName?: string | null;
  genusLatinName?: string | null;
}

const ScenarioFarmCropsPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const navigate = useNavigate();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();

  const crops = useMemo(
    () => buildScenarioCropSummaries(world.structure, undefined, world.runtime?.plantings),
    [world.runtime?.plantings, world.structure],
  );

  const columns: ColumnsType<ScenarioCropSummary> = [
    {
      key: 'name',
      render: (_, record) => (
        <div className="crop-names">
          <Text strong className="crop-name">{record.name}</Text>
          <Text type="secondary" className="crop-latin-name">
            scenario crop · {record.bedIds.length} грядок
          </Text>
        </div>
      ),
    },
    {
      dataIndex: 'plantingsCount',
      key: 'plantingsCount',
      align: 'right',
      width: 60,
      render: (count) => <Text type="secondary" css={css`font-size: 12px;`}>{count || 0}</Text>,
    },
    {
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right',
      width: 90,
      render: (count) => <Tag color="blue">{count || 0} шт</Tag>,
    },
  ];

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Фермы</Link> },
            { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || 'Сценарий'}</Link> },
            { title: 'Культуры' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Alert
          type="info"
          showIcon
          message="Культуры сценария"
          description="Список строится из посадок в scenario world snapshot. Создание и редактирование культур для сценария пока отключены."
        />

        <Card>
          <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
            <div css={css`display: flex; justify-content: space-between; align-items: center;`}>
              <Title level={4} css={css`margin: 0;`}>
                Культуры сценария
              </Title>
              <Button onClick={() => { void refreshWorld(); }}>Обновить</Button>
            </div>
            <Paragraph type="secondary" css={css`margin: 0;`}>
              Это агрегированный список культур, присутствующих в текущем сценарии.
            </Paragraph>
          </Space>
        </Card>

        <Card title={`Культуры (${crops.length})`} styles={{ body: { padding: '12px' } }}>
          {crops.length === 0 ? (
            <Empty description="В сценарии пока нет культур" />
          ) : (
            <Table<ScenarioCropSummary>
              dataSource={crops}
              columns={columns}
              rowKey="routeId"
              pagination={false}
              showHeader={false}
              onRow={(record) => ({
                onClick: () => navigate(`/saves/${saveId}/crops/${record.routeId}`),
                css: css`cursor: pointer;`,
              })}
              className="compact-crops-table"
            />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundCropsPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const navigate = useNavigate();
  const canCreateCrops = useCanIDo('saves.plan.update');

  const {
    data: crops = [],
    isLoading: cropsLoading,
    isError: cropsError,
    refetch: refetchCrops,
  } = useGetSaveCropsQuery({ saveId: saveId || '', limit: 200, offset: 0 }, { skip: !saveId });

  const [createCrop, { isLoading: creatingCrop }] = useCreateSaveCropMutation();

  const { data: taxonomy, isLoading: taxonomyLoading } = useGetSaveCropTaxonomyTreeQuery(saveId || '', { skip: !saveId });

  const [cropModalOpen, setCropModalOpen] = useState(false);

  // Добавляем информацию о семействе и роде к каждой культуре
  const cropsWithTaxonomy = useMemo<CropWithTaxonomy[]>(() => {
    if (!taxonomy) return crops as CropWithTaxonomy[];
    
    return crops.map((crop) => {
      let taxonomyInfo: { 
        familyId?: string | number; 
        familyName?: string; 
        familyLatinName?: string;
        genusId?: string | number;
        genusName?: string;
        genusLatinName?: string;
      } = {};
      
      if (crop.speciesId) {
        for (const family of taxonomy.families) {
          for (const genus of family.genera) {
            const species = genus.species.find(s => String(s.id) === String(crop.speciesId));
            if (species) {
              taxonomyInfo = {
                familyId: family.id,
                familyName: family.name,
                familyLatinName: family.latinName,
                genusId: genus.id,
                genusName: genus.name,
                genusLatinName: genus.latinName,
              };
              break;
            }
          }
          if (taxonomyInfo.familyId) break;
        }
      }
      
      return {
        ...crop,
        ...taxonomyInfo,
      };
    });
  }, [crops, taxonomy]);

  // Группируем культуры по семействам → родам
  const cropsByFamilyAndGenus = useMemo(() => {
    const familyMap = new Map<string, {
      familyId: string | number;
      familyName: string;
      familyLatinName: string;
      genera: Map<string, {
        genusId: string | number;
        genusName: string;
        genusLatinName: string;
        crops: CropWithTaxonomy[];
      }>;
    }>();
    
    const unknownCrops: CropWithTaxonomy[] = [];
    
    cropsWithTaxonomy.forEach((crop) => {
      if (!crop.familyId || !crop.genusId) {
        unknownCrops.push(crop);
        return;
      }
      
      const familyKey = String(crop.familyId);
      const genusKey = String(crop.genusId);
      
      if (!familyMap.has(familyKey)) {
        familyMap.set(familyKey, {
          familyId: crop.familyId,
          familyName: crop.familyName || 'Без названия',
          familyLatinName: crop.familyLatinName || '',
          genera: new Map(),
        });
      }
      
      const family = familyMap.get(familyKey)!;
      
      if (!family.genera.has(genusKey)) {
        family.genera.set(genusKey, {
          genusId: crop.genusId,
          genusName: crop.genusName || 'Без названия',
          genusLatinName: crop.genusLatinName || '',
          crops: [],
        });
      }
      
      family.genera.get(genusKey)!.crops.push(crop);
    });
    
    const families = Array.from(familyMap.values())
      .map(family => ({
        ...family,
        genera: Array.from(family.genera.values()).sort((a, b) => 
          a.genusName.localeCompare(b.genusName)
        ),
      }))
      .sort((a, b) => a.familyName.localeCompare(b.familyName));
    
    return { families, unknownCrops };
  }, [cropsWithTaxonomy]);

  const handleOpenCreateCrop = () => {
    setCropModalOpen(true);
  };

  const handleCloseCropModal = () => {
    setCropModalOpen(false);
  };

  const handleCropSubmit = async (values: any) => {
    if (!saveId) {
      return;
    }

    if (values.speciesId === undefined || values.speciesId === null) {
      return;
    }

    const speciesId = Number(values.speciesId);
    const cultivarId =
      values.cultivarId === undefined || values.cultivarId === null
        ? null
        : Number(values.cultivarId);

    try {
      await createCrop({
        saveId,
        speciesId,
        cultivarId,
        latinName: values.latinName,
        name: values.name,
      }).unwrap();
      toastSuccess('Культура создана');
      handleCloseCropModal();
      refetchCrops();
    } catch (error: any) {
      // Error handled by toast
    }
  };

  const handleRowClick = useCallback((crop: CropWithTaxonomy) => {
    navigate(`/saves/${saveId}/crops/${crop.id}`);
  }, [navigate, saveId]);

  const columns: ColumnsType<CropWithTaxonomy> = [
    {
      key: 'name',
      render: (_, record) => (
        <div className="crop-names">
          <Text strong className="crop-name">{record.name}</Text>
          <Text italic type="secondary" className="crop-latin-name">{record.latinName}</Text>
        </div>
      ),
    },
    {
      dataIndex: 'plantingsCount',
      key: 'plantingsCount',
      align: 'right',
      width: 60,
      render: (count) => <Text type="secondary" css={css`font-size: 12px;`}>{count || 0}</Text>,
    },
  ];

  const saveTitle = save.type === 'free' ? 'Свободная игра' : 'Сценарий';

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Фермы</Link> },
            { title: <Link to={`/saves/${saveId}`}>{saveTitle}</Link> },
            { title: 'Культуры' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
            <div css={css`display: flex; justify-content: space-between; align-items: center;`}>
              <Title level={4} css={css`margin: 0;`}>
                Культуры сейва
              </Title>
              {canCreateCrops && (
                <Button type="primary" onClick={handleOpenCreateCrop}>
                  Добавить культуру
                </Button>
              )}
            </div>
            <Paragraph type="secondary" css={css`margin: 0;`}>
              Это рабочий набор культур текущего сейва. При создании посадки выбираются только эти культуры.
            </Paragraph>
          </Space>
        </Card>

        <Card 
          title={`Культуры сейва (${crops.length})`}
          styles={{ body: { padding: '12px' } }}
        >
          {cropsLoading || taxonomyLoading ? (
            <Spin tip="Загрузка данных..." />
          ) : cropsError ? (
            <Card type="inner">Не удалось загрузить культуры</Card>
          ) : crops.length === 0 ? (
            <Empty description="Культуры пока не добавлены" />
          ) : (
            <>
              {cropsByFamilyAndGenus.families.length > 0 && (
                <Collapse
                  size="small"
                  items={cropsByFamilyAndGenus.families.map((family) => {
                    const totalCrops = family.genera.reduce((sum, genus) => sum + genus.crops.length, 0);
                    return {
                      key: String(family.familyId),
                      label: (
                        <div className="family-header">
                          <div className="family-main">
                            <Text strong className="family-name">{family.familyName}</Text>
                            {family.familyLatinName && (
                              <Text type="secondary" italic className="family-latin">{family.familyLatinName}</Text>
                            )}
                          </div>
                          <Tag color="green">{totalCrops}</Tag>
                        </div>
                      ),
                      styles: { body: { padding: 0 } },
                      children: family.genera.map((genus) => (
                        <div key={String(genus.genusId)} className="genus-section">
                          <div className="genus-header">
                            <Text strong css={css`font-size: 13px;`}>{genus.genusName}</Text>
                            {genus.genusLatinName && (
                              <Text type="secondary" italic css={css`font-size: 12px; margin-left: 8px;`}>{genus.genusLatinName}</Text>
                            )}
                          </div>
                          <Table
                            dataSource={genus.crops}
                            columns={columns}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            showHeader={false}
                            onRow={(record) => ({
                              onClick: () => handleRowClick(record),
                              css: css`cursor: pointer;`,
                            })}
                            className="compact-crops-table"
                          />
                        </div>
                      )),
                    };
                  })}
                />
              )}

              {cropsByFamilyAndGenus.unknownCrops.length > 0 && (
                <Card 
                  title="Без семейства/рода" 
                  type="inner" 
                  size="small"
                  styles={{ body: { padding: '8px' } }}
                >
                  <Table
                    dataSource={cropsByFamilyAndGenus.unknownCrops}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    showHeader={false}
                    onRow={(record) => ({
                      onClick: () => handleRowClick(record),
                      css: css`cursor: pointer;`,
                    })}
                    className="compact-crops-table"
                  />
                </Card>
              )}
            </>
          )}
        </Card>
      </Space>

      <CropFormModal
        open={cropModalOpen}
        editingCrop={null}
        taxonomy={taxonomy}
        taxonomyLoading={taxonomyLoading}
        onSubmit={handleCropSubmit}
        onCancel={handleCloseCropModal}
        isLoading={creatingCrop}
      />
    </MainLayout>
  );
};

const FarmCropsPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioFarmCropsPage />;
  }
  return <FarmBoundCropsPage />;
};

export default FarmCropsPage;
